import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Person as PersonIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { usePermissions } from '../api/auth/PermissionsContext';
import { 
  fetchUserById, 
  updateUser, 
  fetchUserRoles, 
  fetchAvailableRoles, 
  saveUserRoles 
} from '../api/features/users/usersApi';
import { fetchClients } from '../api/features/clientData/clientApi';
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';

export default function UserEdit() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { clientCode } = useClientCode();
  const { permissions } = usePermissions();
  const isAdmin = permissions.includes('ADMIN');

  // State
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [active, setActive] = useState(true);
  const [clientOptions, setClientOptions] = useState<any[]>([]); // { id, code, name } for lookup

  // Role management - same pattern as RoleEdit (Permissions / Assigned Permissions)
  const [roles, setRoles] = useState<any[]>([]);           // all available roles from role/ API
  const [assignedRoles, setAssignedRoles] = useState<any[]>([]);  // user's assigned roles from userRole API

  // Common styles
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: '#fff',
      '& fieldset': { borderColor: '#ddd' },
      '&:hover fieldset': { borderColor: '#999' },
      '&.Mui-focused fieldset': { borderColor: '#1976d2' },
    },
    '& .MuiInputLabel-root': { color: '#666' },
    '& .MuiInputBase-input': { color: '#222' },
  };

  const sectionSx = {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: '8px',
    border: `1px solid ${SAP_COLORS.sapGray2}`,
    boxShadow: SAP_SHADOWS.sm,
    height: '500px',
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
  };

  // Fetch user data
  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  // Fetch client list for code lookup - GET /admin/client/?firstResult=-1&maxResult=0&searched=true
  useEffect(() => {
    fetchClients({ clientCode: 'admin', firstResult: -1, maxResult: 0, searched: true })
      .then((clients: any) => setClientOptions(Array.isArray(clients) ? clients : []))
      .catch(() => setClientOptions([]));
  }, []);

  const displayClientCode = user && user.clientId != null && clientOptions.length > 0
    ? (clientOptions.find((c: any) => c.id === user.clientId || c.id === String(user.clientId))?.code ?? '')
    : '';

  const fetchUserData = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const userData = await fetchUserById({ clientCode, userId: parseInt(userId) });
      setUser(userData);
      setFirstName(userData.firstName);
      setLastName(userData.lastName);
      setEmail(userData.email);
      setUserName(userData.userName);
      setActive(userData.active);

      // Fetch roles
      await fetchRolesData();
    } catch (err) {
      logger.error('Error fetching user:', err);
      toast.error('Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRolesData = async () => {
    if (!userId) return;
    try {
      const [allRolesData, assignedRolesData] = await Promise.all([
        fetchAvailableRoles({ clientCode, firstResult: -1, maxResult: 0 }),
        fetchUserRoles({ clientCode, userId: parseInt(userId), firstResult: -1, maxResult: 0 }),
      ]);
      setRoles(allRolesData);
      const normalizedAssigned = assignedRolesData
        .map((ar: any) => {
          const match = allRolesData.find((r: any) => r.id === ar.id || r.roleName === ar.roleName);
          return match || { id: ar.id, roleName: ar.roleName };
        })
        .filter((r: any) => r.id != null && r.roleName);
      setAssignedRoles(normalizedAssigned);
    } catch (err) {
      logger.error('Error fetching roles:', err);
      toast.error('Failed to fetch roles data');
    }
  };

  const isAssigned = (roleId: number) => assignedRoles.some((ar) => ar.id === roleId);

  const handleAssignSingle = (role: any) => {
    if (isAssigned(role.id)) return;
    setAssignedRoles((prev) => [...prev, role]);
  };

  const handleUnassignSingle = (role: any) => {
    setAssignedRoles((prev) => prev.filter((r) => r.id !== role.id));
  };

  const handleBack = () => {
    navigate('/users');
  };

  const handleUpdate = async () => {
    if (!user || !userId) return;
    setSaving(true);
    try {
      const fullPayload = {
        ...user,
        id: user.id,
        firstName,
        lastName,
        email,
        userName,
        clientId: user.clientId,
        active,
        lastUpdateDate: Date.now(),
        lastUpdatedBy: user.lastUpdatedBy ?? user.createdBy,
        ...(password
          ? {
              password,
              updatePassword: password,
              confirmPassword: password,
            }
          : {}),
      };
      await updateUser({ clientCode, payload: fullPayload });
      await saveUserRoles({
        clientCode,
        payload: { userId, list: assignedRoles.map((r) => r.id) },
      });
      toast.success('User updated successfully');
      fetchUserData();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: SAP_SPACING.lg, bgcolor: SAP_COLORS.sapGray1, minHeight: '100vh' }}>
      {/* Header with back arrow */}
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: SAP_SPACING.xl }}>
        <IconButton
          onClick={handleBack}
          sx={{
            marginRight: SAP_SPACING.md,
            backgroundColor: SAP_COLORS.sapGray2,
            color: SAP_COLORS.sapGray7,
            '&:hover': { backgroundColor: SAP_COLORS.sapGray3 },
          }}
          aria-label="Back to Users"
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: SAP_SPACING.sm }}>
          <PersonIcon sx={{ color: SAP_COLORS.sapBlue, fontSize: '2rem' }} />
          <Box>
            <Typography variant="h4" sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold }}>
              Edit User
            </Typography>
            <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5, fontSize: '0.9375rem' }}>
              {user ? `${user.firstName} ${user.lastName}` : 'Manage user details and roles'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* User Information Section */}
      <Paper sx={{ mb: 3, bgcolor: SAP_COLORS.sapWhite, p: 3, borderRadius: 2, border: `1px solid ${SAP_COLORS.sapGray2}` }}>
        <Typography variant="h6" sx={{ color: '#333', mb: 2, fontWeight: 600 }}>
          User Information
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          <TextField
            label="First Name*"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            sx={{ ...textFieldSx, flex: 1 }}
            fullWidth
          />
          <TextField
            label="Last Name*"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            sx={{ ...textFieldSx, flex: 1 }}
            fullWidth
          />
          <TextField
            label="Client Code"
            value={displayClientCode}
            disabled
            sx={{ ...textFieldSx, flex: 1 }}
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 2 }}>
          <TextField
            label="Email*"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ ...textFieldSx, flex: 1 }}
            fullWidth
          />
          <TextField
            label="Username"
            value={userName}
            disabled
            sx={{ ...textFieldSx, flex: 1 }}
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 2 }}>
          <TextField
            label="Password*"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            sx={{ ...textFieldSx, flex: 1 }}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                sx={{ color: SAP_COLORS.sapBlue }}
              />
            }
            label="Active"
            sx={{ color: SAP_COLORS.sapGray7, flex: 1, alignItems: 'center' }}
          />
        </Box>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Role Management - same pattern as RoleEdit (Roles / Assigned Roles) */}
      {!loading && (
        <>
          <Typography variant="h6" sx={{ color: SAP_COLORS.sapGray7, mb: 2, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold }}>
            Roles
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch' }}>
            {/* Available Roles */}
            <Paper sx={sectionSx}>
              <Box sx={{ 
                bgcolor: SAP_COLORS.sapBlue, 
                color: SAP_COLORS.sapWhite, 
                p: 2, 
                borderTopLeftRadius: 8, 
                borderTopRightRadius: 8,
                fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                fontSize: '0.9375rem',
              }}>
                Roles
              </Box>
              <List sx={{ flex: 1, overflow: 'auto' }}>
                {roles.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No roles available" sx={{ color: SAP_COLORS.sapGray5, textAlign: 'center' }} />
                  </ListItem>
                ) : (
                  roles.map((role) => {
                    const assigned = isAssigned(role.id);
                    return (
                      <ListItem key={role.id} disablePadding>
                        {assigned ? (
                          <ListItemButton
                            disabled
                            sx={{
                              opacity: 0.6,
                              cursor: 'default',
                              '&.Mui-disabled': { opacity: 0.6 },
                            }}
                          >
                            <ListItemText primary={role.roleName} sx={{ color: SAP_COLORS.sapGray5 }} />
                          </ListItemButton>
                        ) : (
                          <ListItemButton
                            onClick={() => handleAssignSingle(role)}
                            sx={{ '&:hover': { bgcolor: SAP_COLORS.sapGray1 } }}
                          >
                            <ListItemText primary={role.roleName} sx={{ color: SAP_COLORS.sapGray7 }} />
                          </ListItemButton>
                        )}
                      </ListItem>
                    );
                  })
                )}
              </List>
            </Paper>

            {/* Assigned Roles */}
            <Paper sx={sectionSx}>
              <Box sx={{ 
                bgcolor: SAP_COLORS.sapBlue, 
                color: SAP_COLORS.sapWhite, 
                p: 2, 
                borderTopLeftRadius: 8, 
                borderTopRightRadius: 8,
                fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                fontSize: '0.9375rem',
              }}>
                Assigned Roles
              </Box>
              <List sx={{ flex: 1, overflow: 'auto' }}>
                {assignedRoles.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No assigned roles" sx={{ color: SAP_COLORS.sapGray5, textAlign: 'center' }} />
                  </ListItem>
                ) : (
                  assignedRoles.map((role) => (
                    <ListItem key={role.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleUnassignSingle(role)}
                        sx={{ '&:hover': { bgcolor: SAP_COLORS.sapGray1 } }}
                      >
                        <ListItemText primary={role.roleName} sx={{ color: SAP_COLORS.sapGray7 }} />
                      </ListItemButton>
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={handleBack} disabled={saving} sx={{ color: SAP_COLORS.sapGray6 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={saving}
              sx={{ bgcolor: SAP_COLORS.sapBlue, '&:hover': { bgcolor: SAP_COLORS.sapBlueDark } }}
            >
              {saving ? 'Saving...' : 'Update'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
} 